/**
 * SKOP AI Alerts
 *
 * AI-generated alerts for token events with natural language explanations.
 * Available to all users (no tier gating).
 */


export type AIAlertCategory =
  | 'whale_movement'
  | 'unusual_volume'
  | 'holder_change'
  | 'social_buzz'
  | 'risk_change'
  | 'creator_activity';

export type AIAlertSeverity = 'info' | 'warning' | 'critical';

export interface AIAlert {
  id: string;
  tokenAddress: string;
  tokenSymbol: string;
  tokenName: string;
  category: AIAlertCategory;
  severity: AIAlertSeverity;
  title: string;
  description: string;
  aiInsight: string;
  data: {
    previousValue?: number;
    currentValue?: number;
    changePercent?: number;
    walletAddress?: string;
    amount?: number;
  };
  createdAt: number;
  read: boolean;
  dismissed: boolean;
  walletAddress: string;
}

export interface AIAlertPreferences {
  enabled: boolean;
  categories: Record<AIAlertCategory, boolean>;
  minSeverity: AIAlertSeverity;
  watchedTokens: string[];
  soundEnabled: boolean;
  browserNotifications: boolean;
}

// Storage keys
const AI_ALERTS_KEY = 'skop_ai_alerts';
const AI_ALERT_PREFS_KEY = 'skop_ai_alert_prefs';

// Alert limits (no tier gating, all users unlimited)
export const AI_ALERT_LIMITS = {
  maxWatchedTokens: -1, // Unlimited
  alertHistory: -1,     // Unlimited
};

// Default preferences
const DEFAULT_PREFERENCES: AIAlertPreferences = {
  enabled: true,
  categories: {
    whale_movement: true,
    unusual_volume: true,
    holder_change: true,
    social_buzz: true,
    risk_change: true,
    creator_activity: true,
  },
  minSeverity: 'info',
  watchedTokens: [],
  soundEnabled: true,
  browserNotifications: true,
};

// ============================================================================
// Alert Management
// ============================================================================

/**
 * Get all AI alerts for a user
 */
export function getAIAlerts(walletAddress: string): AIAlert[] {
  try {
    const stored = localStorage.getItem(`${AI_ALERTS_KEY}_${walletAddress}`);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

/**
 * Save AI alerts
 */
function saveAIAlerts(walletAddress: string, alerts: AIAlert[]): void {
  localStorage.setItem(`${AI_ALERTS_KEY}_${walletAddress}`, JSON.stringify(alerts));
}

/**
 * Get unread alert count
 */
export function getUnreadAlertCount(walletAddress: string): number {
  const alerts = getAIAlerts(walletAddress);
  return alerts.filter(a => !a.read && !a.dismissed).length;
}

/**
 * Mark alert as read
 */
export function markAlertRead(walletAddress: string, alertId: string): void {
  const alerts = getAIAlerts(walletAddress);
  const alert = alerts.find(a => a.id === alertId);
  if (alert) {
    alert.read = true;
    saveAIAlerts(walletAddress, alerts);
  }
}

/**
 * Mark all alerts as read
 */
export function markAllAlertsRead(walletAddress: string): void {
  const alerts = getAIAlerts(walletAddress);
  alerts.forEach(a => { a.read = true; });
  saveAIAlerts(walletAddress, alerts);
}

/**
 * Dismiss alert
 */
export function dismissAlert(walletAddress: string, alertId: string): void {
  const alerts = getAIAlerts(walletAddress);
  const alert = alerts.find(a => a.id === alertId);
  if (alert) {
    alert.dismissed = true;
    saveAIAlerts(walletAddress, alerts);
  }
}

/**
 * Clear all dismissed alerts
 */
export function clearDismissedAlerts(walletAddress: string): void {
  const alerts = getAIAlerts(walletAddress);
  const filtered = alerts.filter(a => !a.dismissed);
  saveAIAlerts(walletAddress, filtered);
}

/**
 * Add a new AI alert
 */
export function addAIAlert(
  walletAddress: string,
  alert: Omit<AIAlert, 'id' | 'createdAt' | 'read' | 'dismissed' | 'walletAddress'>
): AIAlert {
  const newAlert: AIAlert = {
    ...alert,
    id: generateAlertId(),
    createdAt: Date.now(),
    read: false,
    dismissed: false,
    walletAddress,
  };

  const alerts = getAIAlerts(walletAddress);
  alerts.unshift(newAlert); // Add to beginning

  // Trim to max history if needed
  const maxHistory = AI_ALERT_LIMITS.alertHistory;
  if (maxHistory > 0 && alerts.length > maxHistory) {
    alerts.splice(maxHistory);
  }

  saveAIAlerts(walletAddress, alerts);
  return newAlert;
}

// ============================================================================
// Preferences Management
// ============================================================================

/**
 * Get user's alert preferences
 */
export function getAIAlertPreferences(walletAddress: string): AIAlertPreferences {
  try {
    const stored = localStorage.getItem(`${AI_ALERT_PREFS_KEY}_${walletAddress}`);
    if (!stored) return { ...DEFAULT_PREFERENCES };
    return { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) };
  } catch {
    return { ...DEFAULT_PREFERENCES };
  }
}

/**
 * Save user's alert preferences
 */
export function saveAIAlertPreferences(walletAddress: string, prefs: Partial<AIAlertPreferences>): void {
  const current = getAIAlertPreferences(walletAddress);
  const updated = { ...current, ...prefs };
  localStorage.setItem(`${AI_ALERT_PREFS_KEY}_${walletAddress}`, JSON.stringify(updated));
}

/**
 * Add token to watchlist
 */
export function watchToken(walletAddress: string, tokenAddress: string): boolean {
  const prefs = getAIAlertPreferences(walletAddress);
  if (prefs.watchedTokens.includes(tokenAddress)) return false;

  prefs.watchedTokens.push(tokenAddress);
  saveAIAlertPreferences(walletAddress, prefs);
  return true;
}

/**
 * Remove token from watchlist
 */
export function unwatchToken(walletAddress: string, tokenAddress: string): void {
  const prefs = getAIAlertPreferences(walletAddress);
  prefs.watchedTokens = prefs.watchedTokens.filter(t => t !== tokenAddress);
  saveAIAlertPreferences(walletAddress, prefs);
}

/**
 * Check if token is being watched
 */
export function isTokenWatched(walletAddress: string, tokenAddress: string): boolean {
  const prefs = getAIAlertPreferences(walletAddress);
  return prefs.watchedTokens.includes(tokenAddress);
}

// ============================================================================
// Mock Alert Generation (for demo)
// ============================================================================

/**
 * Generate mock AI alerts for demo purposes
 */
export function generateMockAIAlerts(
  walletAddress: string,
  tokenAddress: string,
  tokenSymbol: string,
  tokenName: string
): void {
  const alerts = getAIAlerts(walletAddress);

  // Don't generate if we already have alerts for this token recently
  const recentAlerts = alerts.filter(
    a => a.tokenAddress === tokenAddress && Date.now() - a.createdAt < 60 * 60 * 1000
  );
  if (recentAlerts.length >= 3) return;

  // Random chance of generating alerts
  if (Math.random() > 0.4) return;

  const alertTypes = getRandomAlertTypes();
  for (const type of alertTypes) {
    const alert = generateAlertByType(type, tokenAddress, tokenSymbol, tokenName);
    addAIAlert(walletAddress, alert);
  }
}

/**
 * Get random alert types to generate
 */
function getRandomAlertTypes(): AIAlertCategory[] {
  const allTypes: AIAlertCategory[] = [
    'whale_movement',
    'unusual_volume',
    'holder_change',
    'social_buzz',
    'risk_change',
    'creator_activity',
  ];

  const count = Math.floor(Math.random() * 3) + 1; // 1-3 alerts
  const shuffled = allTypes.sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

/**
 * Generate alert content by type
 */
function generateAlertByType(
  category: AIAlertCategory,
  tokenAddress: string,
  tokenSymbol: string,
  tokenName: string
): Omit<AIAlert, 'id' | 'createdAt' | 'read' | 'dismissed' | 'walletAddress'> {
  switch (category) {
    case 'whale_movement':
      return generateWhaleAlert(tokenAddress, tokenSymbol, tokenName);
    case 'unusual_volume':
      return generateVolumeAlert(tokenAddress, tokenSymbol, tokenName);
    case 'holder_change':
      return generateHolderAlert(tokenAddress, tokenSymbol, tokenName);
    case 'social_buzz':
      return generateSocialAlert(tokenAddress, tokenSymbol, tokenName);
    case 'risk_change':
      return generateRiskAlert(tokenAddress, tokenSymbol, tokenName);
    case 'creator_activity':
      return generateCreatorAlert(tokenAddress, tokenSymbol, tokenName);
  }
}

function generateWhaleAlert(tokenAddress: string, tokenSymbol: string, tokenName: string) {
  const isBuy = Math.random() > 0.5;
  const amount = Math.floor(Math.random() * 50) + 5;
  const severity: AIAlertSeverity = amount > 30 ? 'critical' : amount > 15 ? 'warning' : 'info';

  return {
    tokenAddress,
    tokenSymbol,
    tokenName,
    category: 'whale_movement' as AIAlertCategory,
    severity,
    title: `Whale ${isBuy ? 'Buy' : 'Sell'} Detected`,
    description: `A top holder ${isBuy ? 'bought' : 'sold'} ${amount}% of supply.`,
    aiInsight: isBuy
      ? `This whale has a ${Math.floor(Math.random() * 30) + 60}% win rate on previous trades. Their accumulation often precedes price increases.`
      : `This wallet previously sold before a ${Math.floor(Math.random() * 40) + 20}% price drop. Exercise caution.`,
    data: {
      changePercent: isBuy ? amount : -amount,
      walletAddress: `${tokenAddress.slice(0, 4)}...${tokenAddress.slice(-4)}`,
      amount,
    },
  };
}

function generateVolumeAlert(tokenAddress: string, tokenSymbol: string, tokenName: string) {
  const multiplier = Math.floor(Math.random() * 5) + 2;
  const severity: AIAlertSeverity = multiplier > 5 ? 'critical' : multiplier > 3 ? 'warning' : 'info';

  return {
    tokenAddress,
    tokenSymbol,
    tokenName,
    category: 'unusual_volume' as AIAlertCategory,
    severity,
    title: `Volume Spike: ${multiplier}x Average`,
    description: `24h volume is ${multiplier}x higher than the 7-day average.`,
    aiInsight: `${Math.floor(Math.random() * 30) + 60}% of trades are buys, suggesting accumulation. Watch for continuation or reversal at current levels.`,
    data: {
      previousValue: 100,
      currentValue: 100 * multiplier,
      changePercent: (multiplier - 1) * 100,
    },
  };
}

function generateHolderAlert(tokenAddress: string, tokenSymbol: string, tokenName: string) {
  const isPositive = Math.random() > 0.5;
  const change = Math.floor(Math.random() * 15) + 3;

  return {
    tokenAddress,
    tokenSymbol,
    tokenName,
    category: 'holder_change' as AIAlertCategory,
    severity: 'info' as AIAlertSeverity,
    title: isPositive ? 'Holder Distribution Improving' : 'Holder Concentration Increasing',
    description: isPositive
      ? `Top 10 holder concentration decreased by ${change}%.`
      : `Top 10 holder concentration increased by ${change}%.`,
    aiInsight: isPositive
      ? 'Wider distribution typically indicates healthier token economics and reduced rug risk.'
      : 'Increasing concentration may indicate accumulation by insiders or potential manipulation risk.',
    data: {
      previousValue: isPositive ? 50 + change : 50 - change,
      currentValue: 50,
      changePercent: isPositive ? -change : change,
    },
  };
}

function generateSocialAlert(tokenAddress: string, tokenSymbol: string, tokenName: string) {
  const increase = Math.floor(Math.random() * 300) + 100;
  const sentiment = Math.random() > 0.5 ? 'positive' : 'mixed';

  return {
    tokenAddress,
    tokenSymbol,
    tokenName,
    category: 'social_buzz' as AIAlertCategory,
    severity: 'info' as AIAlertSeverity,
    title: `Social Activity Surge: +${increase}%`,
    description: `Twitter mentions increased ${increase}% in the last 6 hours.`,
    aiInsight: sentiment === 'positive'
      ? `Sentiment analysis shows ${Math.floor(Math.random() * 20) + 70}% positive mentions. Key influencers are discussing this token.`
      : `Mixed sentiment with both bullish calls and caution. Monitor for FUD or coordinated campaigns.`,
    data: {
      previousValue: 100,
      currentValue: 100 + increase,
      changePercent: increase,
    },
  };
}

function generateRiskAlert(tokenAddress: string, tokenSymbol: string, tokenName: string) {
  const isIncrease = Math.random() > 0.4;
  const change = Math.floor(Math.random() * 20) + 5;

  return {
    tokenAddress,
    tokenSymbol,
    tokenName,
    category: 'risk_change' as AIAlertCategory,
    severity: (isIncrease ? 'warning' : 'info') as AIAlertSeverity,
    title: isIncrease ? `Risk Score Increased: +${change} pts` : `Risk Score Decreased: -${change} pts`,
    description: isIncrease
      ? `Risk score increased from ${50 - change} to 50.`
      : `Risk score decreased from ${50 + change} to 50.`,
    aiInsight: isIncrease
      ? 'New risk factors detected: possible bundled wallet activity and concentrated selling from insiders.'
      : 'Improved metrics: better holder distribution and increased organic trading volume.',
    data: {
      previousValue: isIncrease ? 50 - change : 50 + change,
      currentValue: 50,
      changePercent: isIncrease ? change : -change,
    },
  };
}

function generateCreatorAlert(tokenAddress: string, tokenSymbol: string, tokenName: string) {
  const action = Math.random() > 0.5 ? 'added' : 'removed';
  const percent = Math.floor(Math.random() * 10) + 1;

  return {
    tokenAddress,
    tokenSymbol,
    tokenName,
    category: 'creator_activity' as AIAlertCategory,
    severity: (action === 'removed' ? 'warning' : 'info') as AIAlertSeverity,
    title: `Creator Wallet ${action === 'added' ? 'Added' : 'Removed'} Liquidity`,
    description: `Creator ${action} ${percent}% of liquidity.`,
    aiInsight: action === 'removed'
      ? 'Creator removing liquidity could signal reduced commitment. Monitor for further withdrawals.'
      : 'Creator adding liquidity shows continued commitment to the project.',
    data: {
      changePercent: action === 'added' ? percent : -percent,
      amount: percent,
    },
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Generate unique alert ID
 */
function generateAlertId(): string {
  return `ai_alert_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Get category label
 */
export function getCategoryLabel(category: AIAlertCategory): string {
  switch (category) {
    case 'whale_movement': return 'Whale Movement';
    case 'unusual_volume': return 'Volume Alert';
    case 'holder_change': return 'Holder Change';
    case 'social_buzz': return 'Social Buzz';
    case 'risk_change': return 'Risk Change';
    case 'creator_activity': return 'Creator Activity';
  }
}

/**
 * Get category icon identifier (used to select appropriate SVG in UI)
 */
export function getCategoryIcon(category: AIAlertCategory): string {
  switch (category) {
    case 'whale_movement': return 'chart-bar';
    case 'unusual_volume': return 'trending-up';
    case 'holder_change': return 'users';
    case 'social_buzz': return 'message-circle';
    case 'risk_change': return 'alert-triangle';
    case 'creator_activity': return 'user';
  }
}

/**
 * Get severity color
 */
export function getSeverityColor(severity: AIAlertSeverity): string {
  switch (severity) {
    case 'critical': return '#ff3b3b';
    case 'warning': return '#ffb800';
    case 'info': return '#00f0ff';
  }
}

/**
 * Format alert time
 */
export function formatAlertTime(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}
